import React, { useState } from 'react';
import { dayjs } from 'app/dayjs';
import { Box, Collapse } from '@material-ui/core';
import { Currency, CurrencyVariant } from 'components/core';
import {
  ColumnType,
  TimeFilterOption,
  isDateInTimeFilter,
  wrapTableText,
} from 'modules/dashboard/components';
import { TableBody } from 'modules/dashboard/components/dashboard-table/components/table-body/table-body';
import { TableHeader } from 'modules/dashboard/components/dashboard-table/components/table-header/table-header';
import { useTracking, TrackEventName } from 'modules/tracking';
import { MonthlyReturn } from 'modules/dashboard/treasure-reserve/returns/components/returns-chart/returns-chart';
import { useBusiness } from 'hooks/business/use-business';
import { useDividendsAndInterest } from 'hooks/custodian/use-dividends-and-interest';

// TODO: take divider out into a common component or styled file
import { Styled } from 'modules/dashboard/treasure-reserve/portfolio/components/portfolio-view/components/product-table/product-table.style';

const columnsReturnsTable: ColumnType[] = [
  { name: 'Time Period', size: 2 },
  { alignment: 'right', name: 'Ending Balance', size: 2 },
  { alignment: 'right', name: 'Dividend', size: 2 },
  { alignment: 'right', name: '', size: 1 },
  { alignment: 'right', name: 'Interest', size: 2 },
  { alignment: 'right', name: '', size: 1 },
  { alignment: 'right', name: 'Fee', size: 1 },
  { alignment: 'right', name: '', size: 1 },
];

enum MonthlyReturnProperties {
  fees = 'fees',
  net_deposits = 'net_deposits',
  returns_in_dollars = 'returns_in_dollars',
  returns_percentage_annualized = 'returns_percentage_annualized',
  fee_shifted = 'fee_shifted',
  dividends_shifted = 'dividends_shifted',
  interest_shifted = 'interest_shifted',
}

const getTimeFilterTimePeriodText = (timeFilter: TimeFilterOption) => {
  switch (timeFilter) {
    case TimeFilterOption.THREE_MONTH:
      return 'Past 3 Months';
    case TimeFilterOption.SIX_MONTH:
      return 'Past 6 Months';
    case TimeFilterOption.TWELVE_MONTH:
      return 'Past 12 Months';
    case TimeFilterOption.ALL:
      return 'All Time';
  }

  return null;
};

const getTimeFilterRollup = (
  data: MonthlyReturn[],
  timeFilter: TimeFilterOption,
  property: MonthlyReturnProperties,
) => {
  // filter out dates
  return data
    ?.filter((monthlyReturn: MonthlyReturn) =>
      isDateInTimeFilter(
        dayjs(monthlyReturn.start_date).format('YYYY-MM-DD'),
        timeFilter,
      ),
    )
    ?.reduce(
      (sum: number, a: MonthlyReturn) => sum + Number(a[property] || 0),
      0,
    );
};

const showDashInsteadOfZero = (number: number) => {
  if (number === 0) {
    return '-';
  }

  return <Currency number={number} variant={CurrencyVariant.Full} />;
};

const showDate = (date: string | undefined) => {
  if (!date) {
    return null;
  }

  return dayjs.utc(date).format('MMM D');
};

interface ReturnsTableProps {
  data: MonthlyReturn[];

  timeFilter: TimeFilterOption;
}

export const DividendInterestReturnsTable: React.FC<ReturnsTableProps> = ({
  data,
  timeFilter,
}) => {
  const { trackEvent } = useTracking<{
    clickText: string;
    component: string;
    eventName: TrackEventName;
  }>({
    component: 'Return Table',
  });

  const { data: business, isLoading: isLoadingBusiness } = useBusiness();
  // const { data: fees, isLoading: isLoadingFees } = useFees(business?.Id);
  const {
    data: dividendsAndInterest,
    isLoading: isLoadingDividendsAndInterest,
  } = useDividendsAndInterest(business?.Id);

  const handleExpandClick = () => {
    setCollapsed(!collapsed);

    trackEvent({
      clickText: 'Collapse/Expand Data',
      eventName: TrackEventName.Click,
    });
  };

  const [collapsed, setCollapsed] = useState(false);

  const tableData = [];

  const sortedData = data?.sort(
    (a: MonthlyReturn, b: MonthlyReturn) =>
      Number(new Date(b.start_date)) - Number(new Date(a.start_date)),
  );

  // todo: this should be handled in custodian
  // shift fees by 1 month
  sortedData?.map((monthlyReturn: MonthlyReturn, index: number) => {
    // filter out dates
    // because we're not showing the current month, we need to shift 1 month
    if (
      !isDateInTimeFilter(
        dayjs(monthlyReturn.start_date).add(1, 'month').format('YYYY-MM-DD'),
        timeFilter,
      )
    ) {
      return;
    }

    const findData = dividendsAndInterest?.find(
      (x: { start_date: string }) =>
        x.start_date ===
        dayjs(monthlyReturn.start_date)
          .add(1, 'month')
          .startOf('month')
          .format('YYYY-MM-DD'),
    );

    const findInterest = dividendsAndInterest?.find(
      (x: { start_date: string }) =>
        x.start_date ===
        dayjs(monthlyReturn.start_date).startOf('month').format('YYYY-MM-DD'),
    );

    monthlyReturn.fee_shifted = Number(findData?.fees || 0);
    monthlyReturn.fee_date = findData?.fee_date;

    monthlyReturn.dividends_shifted = Number(findData?.dividends || 0);
    monthlyReturn.dividends_date = findData?.dividend_date;

    monthlyReturn.interest_shifted = Number(findInterest?.interest || 0);
    monthlyReturn.interest_date = findInterest?.interest_date;
  });

  if (dividendsAndInterest?.[0]?.interest_date) {
    const interestDate = dayjs(dividendsAndInterest[0].interest_date);

    const findDate = sortedData.find(
      (x) =>
        x.start_date === interestDate.startOf('month').format('YYYY-MM-DD'),
    );

    if (!findDate) {
      sortedData.unshift({
        start_date: interestDate.startOf('month').format('YYYY-MM-DD'),
        end_date: interestDate.endOf('month').format('YYYY-MM-DD'),
        interest_shifted: Number(dividendsAndInterest[0].interest),
        interest_date: dividendsAndInterest[0].interest_date,
        starting_balance: '',
        ending_balance: '',
        net_deposits: '',
        fees: '',
        returns_percentage: '',
        returns_in_dollars: '',
        returns_percentage_annualized: '',
      });
    }
  }

  if (
    timeFilter === TimeFilterOption.THREE_MONTH ||
    timeFilter === TimeFilterOption.SIX_MONTH ||
    timeFilter === TimeFilterOption.TWELVE_MONTH ||
    timeFilter === TimeFilterOption.ALL
  ) {
    const row = [
      wrapTableText(getTimeFilterTimePeriodText(timeFilter)),
      wrapTableText('-'),
      wrapTableText(
        showDashInsteadOfZero(
          getTimeFilterRollup(
            sortedData,
            timeFilter,
            MonthlyReturnProperties.dividends_shifted,
          ),
        ),
      ),
      null,
      wrapTableText(
        showDashInsteadOfZero(
          getTimeFilterRollup(
            sortedData,
            timeFilter,
            MonthlyReturnProperties.interest_shifted,
          ),
        ),
      ),
      null,
      wrapTableText(
        showDashInsteadOfZero(
          getTimeFilterRollup(
            sortedData,
            timeFilter,
            MonthlyReturnProperties.fee_shifted,
          ),
        ),
      ),
      null,
    ];

    tableData.push(row);
  }

  sortedData?.map((monthlyReturn: MonthlyReturn, index: number) => {
    // filter out dates
    // because we're not showing the current month, we need to shift 1 month
    if (
      !isDateInTimeFilter(
        dayjs(monthlyReturn.start_date).add(1, 'month').format('YYYY-MM-DD'),
        timeFilter,
      )
    ) {
      return;
    }

    const row = [
      wrapTableText(dayjs(monthlyReturn.start_date).format('MMMM YYYY')),
      wrapTableText(
        showDashInsteadOfZero(Number(monthlyReturn.ending_balance)),
      ),
      wrapTableText(
        showDashInsteadOfZero(Number(monthlyReturn.dividends_shifted)),
      ),
      wrapTableText(showDate(monthlyReturn.dividends_date)),
      wrapTableText(
        showDashInsteadOfZero(Number(monthlyReturn.interest_shifted)),
      ),
      wrapTableText(showDate(monthlyReturn.interest_date)),
      wrapTableText(showDashInsteadOfZero(Number(monthlyReturn.fee_shifted))),
      wrapTableText(showDate(monthlyReturn.fee_date)),
    ];

    tableData.push(row);
  });
  const showCollapsed = tableData.length > 5;

  return (
    <>
      <TableHeader columns={columnsReturnsTable} />

      <TableBody
        columns={columnsReturnsTable}
        data={showCollapsed ? tableData.slice(0, 4) : tableData}
      />

      {showCollapsed && (
        <>
          <Collapse in={collapsed}>
            <TableBody
              columns={columnsReturnsTable}
              data={tableData.slice(4)}
            />
          </Collapse>

          <Styled.Divider />

          <Box textAlign="center">
            <Styled.CollapseActionBox
              display="inline-block"
              mt={3}
              onClick={handleExpandClick}
            >
              {collapsed ? 'Collapse' : 'Expand'} Data{' '}
              <Box display="inline-block" ml={1} position="relative" top={-2}>
                <Styled.AccordionIcon collapsed={collapsed} />
              </Box>
            </Styled.CollapseActionBox>
          </Box>
        </>
      )}
    </>
  );
};
